import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Ban, Check, Clock, LogIn, LogOut, Search, ShieldCheck, TriangleAlert, X, CalendarClock, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';

// Control de Portería: el vigilante piensa en vehículos, no en formularios.
// Una fila por placa con su semáforo; el estado lo decide el backend
// (porteria.php / porteria_lib.php), la pantalla solo lo muestra.

const STATE_UI = {
  bloqueado: { label: 'Bloqueado', short: 'No puede salir', Icon: Ban, pill: 'bg-red-50 text-red-700 border-red-200', card: 'border-red-200', text: 'text-red-700' },
  pendiente: { label: 'Esperando firma', short: 'Esperando firma', Icon: Clock, pill: 'bg-amber-50 text-amber-800 border-amber-200', card: 'border-amber-200', text: 'text-amber-800' },
  programada: { label: 'Salida programada', short: 'Programada', Icon: CalendarClock, pill: 'bg-slate-50 text-slate-700 border-slate-200', card: 'border-slate-200', text: 'text-slate-700' },
  puede_salir: { label: 'Puede salir', short: 'Puede salir', Icon: Check, pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', card: 'border-emerald-200', text: 'text-emerald-800' },
  salio: { label: 'Salió', short: 'Salió', Icon: LogOut, pill: 'bg-gray-100 text-gray-600 border-gray-200', card: 'border-gray-200', text: 'text-gray-600' },
};
const SEDE_LABELS = { popayan: 'Popayán', emcali: 'Cali', 'santander-de-quilichao': 'Santander de Quilichao' };
const PAGE = 30;

const cleanErr = (e, fb) => String(e?.message || fb).replace(/^HTTP \d+:\s*/, '');

function StatePill({ state, big = false }) {
  const ui = STATE_UI[state] || STATE_UI.salio;
  const { Icon } = ui;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold whitespace-nowrap ${ui.pill} ${big ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs'}`}>
      <Icon size={big ? 16 : 13} aria-hidden="true" /> {ui.label}
    </span>
  );
}

function Plate({ value }) {
  return <span className="inline-block rounded-md border-2 border-gray-900 bg-yellow-300 px-2 py-0.5 font-mono text-sm font-bold tracking-wider text-gray-900">{value}</span>;
}

function Counter({ value, label, Icon, tone }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
      <span className={`flex h-10 w-10 items-center justify-center rounded-full ${tone}`}><Icon size={20} aria-hidden="true" /></span>
      <div>
        <div className="text-2xl font-bold leading-none text-gray-900">{value}</div>
        <div className="mt-1 text-xs text-gray-600">{label}</div>
      </div>
    </div>
  );
}

function VehicleDetail({ sede, plate, onClose, onMoved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.porteria.vehicle({ sede, plate });
      setData(res?.data || null);
    } catch (e) {
      setError(cleanErr(e, 'No fue posible cargar el vehículo.'));
    } finally {
      setLoading(false);
    }
  }, [sede, plate]);
  useEffect(() => { load(); }, [load]);

  const row = data?.row;
  const st = row?.state;
  const register = async (type) => {
    setSaving(true);
    setError('');
    try {
      const fn = type === 'salida' ? api.porteria.registerExit : api.porteria.registerEntry;
      const res = await fn({ sede, plate, notes: notes.trim() });
      // Solo se confirma si el backend guardó el movimiento.
      if (!res?.ok) throw new Error(res?.error || 'No fue posible registrar.');
      onMoved(`${type === 'salida' ? 'Salida' : 'Ingreso'} de ${plate} registrado a las ${String(res.created_at || '').slice(11, 16)} por ${res.guard || 'ti'}.`);
    } catch (e) {
      setError(cleanErr(e, 'No fue posible registrar.'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" role="dialog" aria-modal="true" aria-label={`Detalle ${plate}`} onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <Plate value={plate} />
            <div className="mt-1 text-xs text-gray-500">
              {[data?.vehicle?.vehicle_type, data?.vehicle?.brand].filter(Boolean).join(' · ') || 'Vehículo'} · Sede {SEDE_LABELS[sede] || sede}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border p-2 hover:bg-gray-50" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4 text-sm">
          {loading && <div className="text-gray-500">Cargando…</div>}
          {st && (
            <div className={`rounded-xl border p-3 ${STATE_UI[st.state]?.pill || ''}`}>
              <div className="flex items-center gap-2 text-base font-bold"><StatePill state={st.state} big /></div>
              <p className="mt-2">{st.detail}</p>
              {(st.reasons || []).length > 1 && (
                <ul className="mt-2 list-disc pl-5 text-xs">
                  {st.reasons.map((r, i) => <li key={i}>{r.motivo}: {r.detail}</li>)}
                </ul>
              )}
            </div>
          )}
          {(st?.warnings || []).map((w) => (
            <div key={w} className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><TriangleAlert size={16} className="shrink-0" /> {w}</div>
          ))}
          {data?.vehicle?.sede_doble && (
            <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><TriangleAlert size={16} className="shrink-0" /> Este vehículo tiene asignaciones activas en {data.vehicle.cities.join(' y ')}. Debe tener una sola sede.</div>
          )}
          {row?.driver_name && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Conductor</h4>
              <div className="font-medium text-gray-900">{row.driver_name}</div>
            </section>
          )}
          {data?.preop && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Preoperacional de hoy</h4>
              <div className="rounded-xl border">
                <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
                  <span className="font-medium">{row?.preop?.time ? `Hoy · ${row.preop.time}` : 'Hoy'}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${data.preop.status === 'No Apto' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{data.preop.status}</span>
                </div>
                <ul className="space-y-1 px-3 py-2 text-xs">
                  {data.preop.failed_items.length === 0 && <li className="text-gray-500">Sin ítems fallidos.</li>}
                  {data.preop.failed_items.map((f) => <li key={f} className="flex gap-1.5 text-red-700"><X size={14} className="shrink-0" /> {f}</li>)}
                </ul>
              </div>
            </section>
          )}
          {(data?.preop?.authorizations || []).length > 0 && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Autorizaciones</h4>
              <div className="divide-y rounded-xl border">
                {data.preop.authorizations.map((a) => (
                  <div key={a.key} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div>
                      <div className="font-medium">{a.role}</div>
                      <div className="text-xs text-gray-500">{a.signed ? `${a.name || '—'} · ${String(a.signed_at || '').slice(11, 16)}` : 'Pendiente de firma'}</div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.signed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>{a.signed ? 'Firmado' : 'Pendiente'}</span>
                  </div>
                ))}
                {data.preop.approval && (
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div>
                      <div className="font-medium">Aprobación de preoperacional</div>
                      <div className="text-xs text-gray-500">
                        {data.preop.approval.by || '—'} · {String(data.preop.approval.at || '').slice(11, 16)}
                        {data.preop.approval.notes ? ` · ${data.preop.approval.notes}` : ''}
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${data.preop.approval.status === 'bloqueado' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {data.preop.approval.status === 'bloqueado' ? 'Salida bloqueada' : 'Aprobado'}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}
          {(row?.orders || []).length > 0 && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Salidas de taller sin autorizar</h4>
              <div className="divide-y rounded-xl border">
                {row.orders.map((o) => (
                  <div key={o.id} className="px-3 py-2">
                    <div className="font-medium">{o.order_number} · FT-TRP-130</div>
                    <div className="text-xs text-gray-500">{o.workshop_name || 'Sin taller'} · Salida {o.exit_date}{Number(o.total_cost) > 0 ? ` · $${Number(o.total_cost).toLocaleString('es-CO')}` : ''}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {(data?.movements || []).length > 0 && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Últimos movimientos</h4>
              <ul className="space-y-1 text-xs text-gray-600">
                {data.movements.map((m, i) => (
                  <li key={i}>{String(m.created_at).slice(0, 16)} · {m.movement_type === 'salida' ? 'Salida' : 'Ingreso'} · {m.guard_name || '—'}{m.notes ? ` · ${m.notes}` : ''}</li>
                ))}
              </ul>
            </section>
          )}
          {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
        </div>
        {!loading && row && (
          <div className="space-y-2 border-t p-4">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Observaciones (opcional)" className="w-full rounded-lg border px-3 py-2 text-sm" />
            {st?.state === 'salio' ? (
              <button type="button" disabled={saving} onClick={() => register('ingreso')} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-700 py-3 text-base font-semibold text-white disabled:opacity-60">
                <LogIn size={18} /> {saving ? 'Registrando…' : 'Registrar ingreso'}
              </button>
            ) : (
              <button type="button" disabled={saving || !st?.can_exit} onClick={() => register('salida')} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 py-3 text-base font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500">
                <LogOut size={18} /> {saving ? 'Registrando…' : (st?.can_exit ? 'Registrar salida' : 'Registrar salida · bloqueado')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// extraTabs: otras bandejas del módulo (firmas, visitantes…) que se
// muestran como pestañas en la misma barra, para no tener que bajar a
// buscarlas: [{ key, label, count, tone, hidden, content }]. Su contenido
// queda montado aunque no esté activa, para que cargue y reporte su conteo.
export default function PorteriaControl({ location, extraTabs = [] }) {
  const { currentUser } = useUserPrefs();
  const sede = String(location || '').toLowerCase();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('hoy');
  const [q, setQ] = useState('');
  const [motivo, setMotivo] = useState('');
  const [estado, setEstado] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [detail, setDetail] = useState(null);
  const [flash, setFlash] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    if (!SEDE_LABELS[sede]) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.porteria.board({ sede, tab });
      setBoard(res);
      setUpdatedAt(new Date());
    } catch (e) {
      setError(cleanErr(e, 'No fue posible cargar portería.'));
    } finally {
      setLoading(false);
    }
  }, [sede, tab]);
  useEffect(() => { load(); }, [load]);
  // Las autorizaciones que firmen otros roles se reflejan sin recargar.
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { setShown(PAGE); }, [tab, q, motivo, estado]);
  const isExtra = extraTabs.some((t) => t.key === tab);
  // Si una pestaña extra deja de aplicar (ej. sin rol), volver a Hoy.
  useEffect(() => {
    const t = extraTabs.find((x) => x.key === tab);
    if (t && t.hidden) setTab('hoy');
  }, [extraTabs, tab]);

  const rows = useMemo(() => {
    let list = board?.data || [];
    if (tab === 'bloqueados') list = list.filter((r) => r.state.state === 'bloqueado');
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((r) => [r.plate, r.driver_name, ...(r.orders || []).map((o) => o.order_number), r.authorized_order?.order_number]
        .some((v) => String(v || '').toLowerCase().includes(term)));
    }
    if (motivo) list = list.filter((r) => (r.state.reasons || []).some((x) => x.motivo === motivo));
    if (estado) list = list.filter((r) => r.state.state === estado);
    return list;
  }, [board, tab, q, motivo, estado]);

  if (!SEDE_LABELS[sede]) return null;
  const c = board?.counts || {};
  const minsAgo = updatedAt ? Math.max(0, Math.round((Date.now() - updatedAt.getTime()) / 60000)) : null;
  const todayLabel = new Date().toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-800 text-white"><ShieldCheck size={22} aria-hidden="true" /></span>
          <div>
            <div className="text-xs text-gray-500">Seguridad › Portería</div>
            <h3 className="text-xl font-bold text-gray-900">Control de Portería</h3>
          </div>
        </div>
        <div className="text-right text-xs text-gray-600">
          <span className="inline-block rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-800">Sede {SEDE_LABELS[sede]}</span>
          <div className="mt-1"><span className="font-semibold text-gray-900">{currentUser?.name || board?.guard || ''}</span> · {todayLabel}</div>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Counter value={c.puede_salir ?? '–'} label="Pueden salir" Icon={Check} tone="bg-emerald-100 text-emerald-700" />
        <Counter value={c.bloqueado ?? '–'} label="Bloqueados" Icon={Ban} tone="bg-red-100 text-red-700" />
        <Counter value={c.pendiente ?? '–'} label="Esperando autorización" Icon={Clock} tone="bg-amber-100 text-amber-700" />
        <Counter value={c.salieron_hoy ?? '–'} label="Salieron hoy" Icon={LogOut} tone="bg-sky-100 text-sky-700" />
      </div>

      {flash && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          <span className="flex items-center gap-2"><Check size={16} /> {flash}</span>
          <button type="button" onClick={() => setFlash('')} aria-label="Cerrar aviso"><X size={16} /></button>
        </div>
      )}

      <div className={`mb-3 grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-white p-3 md:grid-cols-[1fr_180px_180px] ${isExtra ? 'hidden' : ''}`}>
        <label className="relative block">
          <span className="sr-only">Buscar vehículo</span>
          <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Placa, orden de taller o conductor · ej. WDH719" className="w-full rounded-lg border-2 border-emerald-700/60 py-2.5 pl-10 pr-3 text-base focus:border-emerald-700 focus:outline-none" />
        </label>
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} className="rounded-lg border px-3 py-2.5 text-sm" aria-label="Motivo">
          <option value="">Motivo: todos</option>
          <option value="Preoperacional">Preoperacional</option>
          <option value="Salida de taller">Salida de taller</option>
        </select>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} className="rounded-lg border px-3 py-2.5 text-sm" aria-label="Estado">
          <option value="">Estado: todos</option>
          {Object.entries(STATE_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex gap-1 overflow-x-auto border-b px-2" role="tablist">
          {[
            ['hoy', 'Hoy', (board?.data || []).length, 'bg-emerald-100 text-emerald-800'],
            ['bloqueados', 'Bloqueados', c.bloqueado, 'bg-red-100 text-red-700'],
            ['vencidos', 'Vencidos sin cerrar', c.vencidos, 'bg-gray-100 text-gray-700'],
            ['historial', 'Historial', null, ''],
            ...extraTabs.filter((t) => !t.hidden).map((t) => [t.key, t.label, t.count ?? null, t.tone || 'bg-amber-100 text-amber-800']),
          ].map(([key, label, n, tone]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium ${tab === key ? 'border-emerald-700 text-emerald-800' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              {label}{n != null && <span className={`rounded-full px-1.5 text-xs ${tone}`}>{n}</span>}
            </button>
          ))}
          <button type="button" onClick={load} className="ml-auto flex items-center gap-1 px-2 text-xs text-gray-500 hover:text-gray-800" aria-label="Actualizar"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
        </div>

        {error && !isExtra && <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {extraTabs.map((t) => (
          <div key={t.key} className={tab === t.key ? 'p-3' : 'hidden'}>{t.content}</div>
        ))}

        {(tab === 'hoy' || tab === 'bloqueados') && (
          <>
            {!loading && rows.length === 0 && <div className="p-6 text-center text-sm text-gray-500">No hay vehículos para los filtros actuales.</div>}
            {/* Escritorio: tabla */}
            <table className="hidden w-full text-sm md:table">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-2">Placa</th><th className="px-4 py-2">Conductor</th><th className="px-4 py-2">Motivo de salida</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2">Detalle</th><th className="px-4 py-2 text-right">Acción</th></tr>
              </thead>
              <tbody>
                {rows.slice(0, shown).map((r) => (
                  <tr key={r.vehicle_id} className="border-t align-top">
                    <td className="px-4 py-3"><Plate value={r.plate} />{r.sede_doble && <div className="mt-1 text-[11px] text-amber-700">Sede doble</div>}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.driver_name || <span className="text-gray-400">Sin conductor</span>}</td>
                    <td className="px-4 py-3"><div className="font-semibold">{r.state.motivo}</div><div className="text-xs text-gray-500">{r.state.motivo === 'Preoperacional' ? (r.preop?.time ? `Hoy ${r.preop.time}` : 'Hoy') : (r.orders?.[0]?.order_number || r.authorized_order?.order_number || '')}</div></td>
                    <td className="px-4 py-3"><StatePill state={r.state.state} /></td>
                    <td className={`px-4 py-3 text-xs ${STATE_UI[r.state.state]?.text || ''}`}>{r.state.detail}{r.state.warnings?.length ? <div className="mt-1 text-amber-700">⚠ {r.state.warnings[0]}</div> : null}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => setDetail(r.plate)} className={`rounded-lg px-3 py-2 text-sm font-semibold ${r.state.can_exit ? 'bg-emerald-700 text-white hover:bg-emerald-800' : 'border bg-white hover:bg-gray-50'}`}>
                        {r.state.can_exit ? 'Registrar salida' : (r.state.state === 'salio' ? 'Registrar ingreso' : 'Ver motivo')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Celular / tablet: tarjetas */}
            <div className="space-y-2 p-2 md:hidden">
              {rows.slice(0, shown).map((r) => (
                <button key={r.vehicle_id} type="button" onClick={() => setDetail(r.plate)} className={`block w-full rounded-xl border bg-white p-3 text-left ${STATE_UI[r.state.state]?.card || ''}`}>
                  <div className="flex items-center justify-between gap-2"><Plate value={r.plate} /><StatePill state={r.state.state} /></div>
                  <div className="mt-2 font-semibold text-gray-900">{r.driver_name || 'Sin conductor'}</div>
                  <div className={`text-sm ${STATE_UI[r.state.state]?.text || ''}`}>{r.state.motivo} · {r.state.detail}</div>
                  {r.state.can_exit && <div className="mt-2 rounded-lg bg-emerald-700 py-2.5 text-center text-base font-semibold text-white">Registrar salida</div>}
                </button>
              ))}
            </div>
            {rows.length > shown && (
              <div className="border-t p-3 text-center"><button type="button" onClick={() => setShown((n) => n + PAGE)} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Ver {Math.min(PAGE, rows.length - shown)} más de {rows.length - shown}</button></div>
            )}
          </>
        )}

        {tab === 'vencidos' && (
          <div className="p-3 text-sm">
            <p className="mb-2 text-xs text-gray-500">Salidas de taller con más de 7 días sin autorizar. No bloquean la operación de hoy; deben revisarse y cerrarse con Transportes.</p>
            {(board?.expired || []).length === 0 && <div className="p-4 text-center text-gray-500">No hay registros vencidos.</div>}
            <ul className="divide-y">
              {(board?.expired || []).filter((o) => !q || String(o.plate).toLowerCase().includes(q.toLowerCase())).map((o) => (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="flex items-center gap-2"><Plate value={o.plate} /> {o.order_number} · {o.workshop_name || 'Sin taller'}</span>
                  <span className="text-xs text-gray-500">Salida registrada {o.exit_date}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === 'historial' && (
          <div className="p-3 text-sm">
            {(board?.history || []).length === 0 && <div className="p-4 text-center text-gray-500">Aún no hay movimientos registrados en esta sede.</div>}
            <ul className="divide-y">
              {(board?.history || []).filter((m) => !q || String(m.plate).toLowerCase().includes(q.toLowerCase())).map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="flex items-center gap-2"><Plate value={m.plate} /> {m.movement_type === 'salida' ? 'Salida' : 'Ingreso'}{m.notes ? ` · ${m.notes}` : ''}</span>
                  <span className="text-xs text-gray-500">{String(m.created_at).slice(0, 16)} · {m.guard_name || '—'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`flex justify-between border-t px-4 py-2 text-xs text-gray-500 ${isExtra ? 'hidden' : ''}`}>
          <span>Vehículos de hoy en sede {SEDE_LABELS[sede]} · ordenados por prioridad</span>
          <span>{minsAgo == null ? '' : `Actualizado hace ${minsAgo} min`}</span>
        </div>
      </div>

      {detail && (
        <VehicleDetail sede={sede} plate={detail} onClose={() => setDetail(null)} onMoved={(msg) => { setDetail(null); setFlash(msg); load(); }} />
      )}
    </div>
  );
}
